import config from "../../config";
import {
  AmlCategory,
  CODE,
  searchApiTypes,
  MIN_INDEX,
  NO_TO_ADD,
} from "../../modules/aml/aml.constant";
import type {
  AmlScreenMatchDecisions,
  Decision,
  numberNullUndefinedType,
  SearchValues,
  stringNullUndefinedType,
} from "../../modules/aml/types/aml.type";
import type { AddressesPayload } from "../../modules/profiles/types/address.type";
import { HitDecisions } from "../../modules/profiles/types/amlScreen.type";
import type {
  AddressType,
  Profile,
} from "../../modules/profiles/types/profile.type";
import {
  amlIndividualDetailProfileHits,
  amlScreenBusinessTableHeadings,
  apiVersion,
  ProfileTabsTypes,
  SearchType,
  NotEnoughCreditsAvailable,
} from "../../shared/constants/common.constant";
import type { ProblemDetails } from "../../shared/types/problem-details.type";
import type { Pagination } from "../../shared/types/table.type";
import type { User } from "../../shared/types/user.type";
import { containsText, createQueryString, getContactName } from "../../utils";

export const getAmlSearchApiUrl = (id: string, selectedTab: string): string => {
  let apiUrl = "";
  if (
    selectedTab === ProfileTabsTypes.business ||
    selectedTab === ProfileTabsTypes.individual
  ) {
    apiUrl = `profiles/${id}/searches`;
  } else if (
    selectedTab === ProfileTabsTypes.keyParties ||
    selectedTab === ProfileTabsTypes.ubos
  ) {
    apiUrl = `profiles/${id}/keyparties/searches`;
  }
  return apiUrl;
};

export const getApiHitProfileType = (type: string): string => {
  return type === SearchType.Individual
    ? searchApiTypes.individual
    : searchApiTypes.business;
};

export const getAmlSearchHitApiUrl = (
  searchId: string,
  selectedTab: string,
  hitProfileType: string,
  query: Pagination | object
): string => {
  let apiUrl = `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/`;
  const apiHitProfileType = getApiHitProfileType(hitProfileType);

  if (
    selectedTab === ProfileTabsTypes.business ||
    selectedTab === ProfileTabsTypes.individual ||
    selectedTab === ProfileTabsTypes.keyParties ||
    selectedTab === ProfileTabsTypes.ubos
  ) {
    apiUrl += `${apiHitProfileType}/${searchId}/hits?${createQueryString(
      query
    )}`;
  }
  return apiUrl;
};

export const getAmlAlertApiUrl = (
  searchId: string,
  hitId: string,
  profileType: string
): string => {
  let apiUrl = `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/`;
  const apiHitProfileType = getApiHitProfileType(profileType);
  apiUrl += `${apiHitProfileType}/${searchId}/hits/${hitId}`;
  return apiUrl;
};

export const randomSingleDigitNumber = (): number => {
  const array = new Uint32Array(1);
  globalThis.crypto.getRandomValues(array);
  return array[0] / (CODE + 1);
};

export const stringCheck = (value: string | null | undefined): string => {
  return value ? `${value}, ` : "";
};

export const removeTrailingCharacter = (str: string): string => {
  return str.replace(/[,\s]+$/g, "");
};

export const buildAddress = (
  address: AddressType | undefined,
  countryCode: string | null | undefined
): string => {
  if (address) {
    const postalAddress = `${stringCheck(address.description)}
      ${stringCheck(address.buildingDetails)}
      ${stringCheck(address.street)}
      ${stringCheck(address.city)}
      ${stringCheck(address.region)}
      ${stringCheck(address.postalCode)}
      ${stringCheck(countryCode)}`;
    return removeTrailingCharacter(postalAddress);
  } else return "";
};

export const tradingAddressPayloadValidation = (
  tradingAddressPayload: AddressesPayload
): boolean => {
  return (
    tradingAddressPayload.countryCode != null ||
    tradingAddressPayload.buildingDetails != "" ||
    tradingAddressPayload.street != "" ||
    tradingAddressPayload.city != "" ||
    tradingAddressPayload.region != "" ||
    tradingAddressPayload.postalCode != ""
  );
};

export const isProfileLocked = (profileData: Profile): boolean => {
  return profileData.isLocked;
};

const insertChangeDecisionHeadingAfterMatchPercentage = (
  headings: string[],
  changeDecisionKey: string
): string[] => {
  const idx = headings.findIndex((h) => h.includes("matchPercentage"));
  if (idx === -1) {
    return [...headings, changeDecisionKey];
  }
  return [
    ...headings.slice(0, idx + 1),
    changeDecisionKey,
    ...headings.slice(idx + 1),
  ];
};

export const getAmlHitsCategoriesTableHeadings = (
  categoryType: HitDecisions,
  isIndividual: boolean
): string[] => {
  const matchDecision = {
    business: "aml:amlBusinessProfileHits.matchDecision",
    individual: "aml:amlIndividualProfileHits.matchDecision",
  };

  const changeDecisionHeading = isIndividual
    ? "aml:amlIndividualProfileHits.changeDecision"
    : "aml:amlBusinessProfileHits.changeDecision";

  const matchPercentageAndDecision = {
    business: [
      "aml:amlBusinessProfileHits.matchPercentage",
      "aml:amlBusinessProfileHits.matchDecision",
    ],
    individual: [
      "aml:amlIndividualProfileHits.matchPercentage",
      "aml:amlIndividualProfileHits.matchDecision",
    ],
  };

  const matchDecisionKey = isIndividual ? "individual" : "business";

  const headings = isIndividual
    ? amlIndividualDetailProfileHits
    : amlScreenBusinessTableHeadings;

  const filterHeadings = (excludeItems: string[]): string[] =>
    headings.filter((item) => !excludeItems.includes(item));

  switch (categoryType) {
    case HitDecisions.undecided:
      return headings;
    case HitDecisions.trueMatch:
    case HitDecisions.falsePositive:
      return insertChangeDecisionHeadingAfterMatchPercentage(
        filterHeadings([matchDecision[matchDecisionKey]]),
        changeDecisionHeading
      );
    case HitDecisions.discarded:
      return filterHeadings(matchPercentageAndDecision[matchDecisionKey]);
    default:
      return headings;
  }
};

export const extractIdFromUrl = (
  urlString: string,
  segment: string,
  requiredIdIndex: number
): string | null => {
  const url = new URL(urlString);

  const pathSegments = url.pathname.split("/");

  const index = pathSegments.indexOf(segment);
  return index !== MIN_INDEX && pathSegments[index + NO_TO_ADD]
    ? pathSegments[index + requiredIdIndex]
    : null;
};

export const getCountryCodeFromSearchValues = (
  searchValues: SearchValues
): string[] => {
  const countryCode = searchValues.countryCode
    ? [searchValues.countryCode]
    : [];
  return countryCode;
};

export const returnEmptyStringIfNullOrUndefined = (
  value: stringNullUndefinedType,
  returnValue: string | null = ""
): string | null => {
  return value || returnValue;
};

export const returnEmptyStringIfNullOrUndefinedDate = (
  value: string | Date | null | undefined,
  returnValue = ""
): string | Date => {
  return value ?? returnValue;
};

export const returnStringIfItsNumberType = (
  value: numberNullUndefinedType,
  returnValue = ""
): string | number => {
  return value || returnValue;
};

export const getValueOrDefault = <T>(
  value: T | null | undefined,
  defaultValue: T
): T => {
  return value ?? defaultValue;
};

export const filterUsersBySearchText = (
  usersList: User[] | undefined,
  searchText: string
): User[] | undefined => {
  return usersList?.filter((option) =>
    containsText(searchText, getContactName(option))
  );
};

export const arePepCategoriesIncludedAndStatusesAreEmpty = (
  categories: string[],
): boolean => {
  return (
    [AmlCategory.pep, AmlCategory.pepLinked].every((value) =>
      categories.includes(value)
    )
  );
};

export const isDecisionMatch = (
  decision: Decision,
  expectedDecision: AmlScreenMatchDecisions
): boolean => {
  return decision === expectedDecision;
};

export const checkIsCreditsInsufficient = (
  problemDetails: ProblemDetails
): boolean => {
  return problemDetails?.details?.type === NotEnoughCreditsAvailable || problemDetails.type === NotEnoughCreditsAvailable;
};

export const areNamesOrCategoriesEmpty = (
  names: string[],
  categories: string[]
): boolean => {
  return !names.length || !categories.length;
};

export const setNumberOrNull = (
  value: number | null
): number | null | undefined => {
  return value ?? null;
};

export const setValueOrNull = (
  value: string | null
): string | null | undefined => {
  return value ?? null;
};

export const getStringValue = (value?: string | null): string =>
  value?.trim() ? value : "";

export const setArrayOrEmpty = <T>(value: T[] | null | undefined): T[] => {
  return value ?? [];
};

export const getStringLength = (value: string | null | undefined): number => {
  if (value != null && value.trim() !== "") {
    return value.length;
  }
  return 0;
};
